import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Grid,
  Chip,
  Tooltip,
  useTheme,
  alpha,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
  Today as TodayIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachMonthOfInterval, isSameMonth } from 'date-fns';

/**
 * SnapshotCalendarSelector - Calendar-style selector for snapshot months
 * Features:
 * - Calendar grid view for month selection
 * - Available snapshots highlighted
 * - Quick navigation between years/months
 * - Live data option
 * - Responsive design
 */
const SnapshotCalendarSelector = ({
  availableSnapshots = [],
  selectedSnapshot = null,
  onSnapshotSelect,
  onLiveDataSelect,
  loading = false,
  sx = {}
}) => {
  const theme = useTheme();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  // Convert snapshot months to Date objects for comparison
  const snapshotDates = availableSnapshots.map(snapshot => {
    const [year, month] = snapshot.split('-').map(Number);
    return new Date(year, month - 1, 1);
  });

  // Generate months for the current view (12 months from current date)
  const generateMonths = (date) => {
    const start = startOfMonth(subMonths(date, 6));
    const end = endOfMonth(addMonths(date, 5));
    return eachMonthOfInterval({ start, end });
  };

  const months = generateMonths(currentDate);

  // Check if a month has snapshot data
  const hasSnapshot = (date) => {
    return snapshotDates.some(snapshotDate => isSameMonth(snapshotDate, date));
  };

  // Get snapshot label for a month
  const getSnapshotLabel = (date) => {
    const snapshotDate = snapshotDates.find(snapshotDate => isSameMonth(snapshotDate, date));
    if (snapshotDate) {
      return format(snapshotDate, 'yyyy-MM');
    }
    return null;
  };

  // Handle month selection
  const handleMonthSelect = (date) => {
    const snapshotMonth = getSnapshotLabel(date);
    if (snapshotMonth) {
      onSnapshotSelect?.(snapshotMonth);
      setSelectedDate(date);
      setShowCalendar(false);
    }
  };

  // Handle live data selection
  const handleLiveDataSelect = () => {
    onLiveDataSelect?.();
    setSelectedDate(null);
    setShowCalendar(false);
  };

  // Navigate to previous/next year
  const navigateYear = (direction) => {
    setCurrentDate(prev => addMonths(prev, direction * 12));
  };

  // Get current selection display
  const getCurrentSelection = () => {
    if (selectedSnapshot) {
      const [year, month] = selectedSnapshot.split('-').map(Number);
      return format(new Date(year, month - 1, 1), 'MMM yyyy');
    }
    return '📈 Live Data (Current)';
  };

  return (
    <Box sx={{ ...sx }}>
      {/* Main Selector Button */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          cursor: 'pointer',
          transition: 'all 0.2s',
          backgroundColor: 'background.paper',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
            transform: 'translateY(-1px)',
            boxShadow: theme.shadows[4]
          }
        }}
        onClick={() => setShowCalendar(true)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarIcon sx={{ color: 'primary.main', fontSize: 20 }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {getCurrentSelection()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {selectedSnapshot ? (
              <HistoryIcon sx={{ fontSize: 16, color: 'warning.main' }} />
            ) : (
              <RefreshIcon sx={{ fontSize: 16, color: 'success.main' }} />
            )}
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {availableSnapshots.length} snapshots
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Calendar Dialog */}
      <Dialog
        open={showCalendar}
        onClose={() => setShowCalendar(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              📅 Select Snapshot Month
            </Typography>
            <IconButton
              onClick={() => setShowCalendar(false)}
              sx={{ color: 'white' }}
              size="small"
            >
              <Typography variant="h6">×</Typography>
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {/* Live Data Option */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 2,
              border: '2px solid',
              borderColor: selectedSnapshot ? 'divider' : 'success.main',
              backgroundColor: selectedSnapshot ? 'background.paper' : alpha(theme.palette.success.main, 0.1),
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'success.main',
                backgroundColor: alpha(theme.palette.success.main, 0.1),
                transform: 'translateY(-1px)'
              }
            }}
            onClick={handleLiveDataSelect}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <RefreshIcon sx={{ color: 'success.main', fontSize: 24 }} />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  📈 Live Data (Current)
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Real-time analytics and current data
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Year Navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <IconButton onClick={() => navigateYear(-1)} size="small">
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {format(currentDate, 'yyyy')}
            </Typography>
            <IconButton onClick={() => navigateYear(1)} size="small">
              <ChevronRightIcon />
            </IconButton>
          </Box>

          {/* Calendar Grid */}
          <Grid container spacing={1}>
            {months.map((month, index) => {
              const hasData = hasSnapshot(month);
              const isSelected = selectedSnapshot && getSnapshotLabel(month) === selectedSnapshot;
              const isCurrentMonth = isSameMonth(month, new Date());

              return (
                <Grid item xs={4} sm={3} key={index}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      backgroundColor: isSelected 
                        ? alpha(theme.palette.primary.main, 0.1)
                        : hasData 
                          ? alpha(theme.palette.warning.main, 0.1)
                          : 'background.paper',
                      cursor: hasData ? 'pointer' : 'default',
                      transition: 'all 0.2s',
                      opacity: hasData ? 1 : 0.5,
                      '&:hover': hasData ? {
                        borderColor: 'primary.main',
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        transform: 'translateY(-1px)',
                        boxShadow: theme.shadows[2]
                      } : {},
                      position: 'relative'
                    }}
                    onClick={() => hasData && handleMonthSelect(month)}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600,
                          color: isSelected ? 'primary.main' : 'text.primary'
                        }}
                      >
                        {format(month, 'MMM')}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'text.secondary',
                          display: 'block',
                          mt: 0.5
                        }}
                      >
                        {format(month, 'yyyy')}
                      </Typography>
                      
                      {/* Status Indicators */}
                      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        {hasData && (
                          <Chip
                            label="📊"
                            size="small"
                            sx={{ 
                              height: 20,
                              fontSize: '0.7rem',
                              backgroundColor: alpha(theme.palette.warning.main, 0.2),
                              color: 'warning.dark'
                            }}
                          />
                        )}
                        {isCurrentMonth && (
                          <Chip
                            label="●"
                            size="small"
                            sx={{ 
                              height: 20,
                              fontSize: '0.7rem',
                              backgroundColor: alpha(theme.palette.success.main, 0.2),
                              color: 'success.dark'
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>

          {/* Legend */}
          <Box sx={{ mt: 3, p: 2, backgroundColor: alpha(theme.palette.grey[100], 0.5), borderRadius: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Legend:
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: 'success.main' }} />
                <Typography variant="caption">Current Month</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: 'warning.main' }} />
                <Typography variant="caption">Has Snapshot Data</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: 'primary.main' }} />
                <Typography variant="caption">Selected</Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setShowCalendar(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SnapshotCalendarSelector; 